import * as React from "react";
import DataTable, { TableColumn } from "react-data-table-component";
import moment from "moment-timezone";
import { Form } from "react-bootstrap";

// Types
// Columns marked hide: MOBILE_BREAKPOINT drop out at or below this width so the
// table fits a phone. Candidate, Status and Actions stay. Matches
// ApplicantsTable and the page stylesheet.
const MOBILE_BREAKPOINT = 768;

interface Profile {
  id: number;
  fullname: string;
  first_name?: string;
  last_name?: string;
  city?: string;
  state?: string;
  avatar_url?: string;
}

interface Job {
  id: number;
  title?: string;
  position_type?: string;
  job_type?: {
    id: number;
    title: string;
  };
}

interface SentInvitation {
  id: number;
  profile: Profile;
  job: Job;
  created_at: string;
  seen: boolean;
  applied: boolean;
  seen_date?: string;
  applied_date?: string;
}

interface SentInvitationsProps {
  invitations: SentInvitation[];
  timezone: string;
  totalCount: number;
  currentPage: number;
  perPage: number;
}

// Filter Bar Component
interface FilterBarProps {
  filters: {
    nameFilter: string;
    statusFilter: string;
  };
  onFiltersChange: (filters: any) => void;
}

function FilterBar({ filters, onFiltersChange }: FilterBarProps) {
  const clearAllFilters = () => {
    onFiltersChange({
      nameFilter: "",
      statusFilter: ""
    });
  };

  const hasActiveFilters = filters.nameFilter || filters.statusFilter;

  return (
    <div
      style={{
        background: 'white',
        border: '1px solid #e8ecef',
        borderRadius: '0.75rem',
        padding: '0.875rem',
        marginBottom: '1.5rem',
        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.06)'
      }}
    >
      <div className="d-flex justify-content-between align-items-center flex-wrap" style={{ gap: '0.75rem' }}>
        <div className="d-flex gap-3 flex-wrap align-items-center">
          <div style={{ width: "250px", position: "relative", marginBottom: 0 }}>
            <Form.Group style={{ marginBottom: 0 }}>
              <div className="position-relative">
                <Form.Control
                  type="text"
                  placeholder="Filter by Name"
                  value={filters.nameFilter}
                  onChange={(e) => onFiltersChange({ ...filters, nameFilter: e.target.value })}
                  style={{
                    marginBottom: 0,
                    borderRadius: '0.5rem',
                    border: '1px solid #e8ecef',
                    padding: '0.5rem 0.75rem',
                    fontSize: '0.95rem',
                    transition: 'all 0.3s ease'
                  }}
                />
                {filters.nameFilter && (
                  <div
                    style={{
                      position: "absolute",
                      right: "10px",
                      top: "50%",
                      transform: "translateY(-50%)",
                      cursor: "pointer",
                      zIndex: 5,
                      color: '#6c757d'
                    }}
                    onClick={() => onFiltersChange({ ...filters, nameFilter: "" })}
                  >
                    <i className="fa fa-times" aria-hidden="true"></i>
                  </div>
                )}
              </div>
            </Form.Group>
          </div>
          <div style={{ width: "180px", position: "relative", marginBottom: 0 }}>
            <Form.Group style={{ marginBottom: 0 }}>
              <div className="position-relative">
                <Form.Select
                  value={filters.statusFilter}
                  onChange={(e) => onFiltersChange({ ...filters, statusFilter: e.target.value })}
                  style={{
                    marginBottom: 0,
                    borderRadius: '0.5rem',
                    border: '1px solid #e8ecef',
                    padding: '0.5rem 0.75rem',
                    fontSize: '0.95rem',
                    transition: 'all 0.3s ease'
                  }}
                >
                  <option value="">All Status</option>
                  <option value="applied">Applied</option>
                  <option value="seen">Seen</option>
                  <option value="pending">Pending</option>
                </Form.Select>
              </div>
            </Form.Group>
          </div>
          <button
            className="btn-modern btn-modern--ghost btn-modern--sm"
            onClick={clearAllFilters}
            style={{
              opacity: hasActiveFilters ? 1 : 0.6
            }}
            disabled={!hasActiveFilters}
          >
            <i className="fa fa-times"></i> Clear filters
          </button>
        </div>
      </div>
    </div>
  );
}

// Status Badge Component
function StatusBadge({ applied, seen }: { applied: boolean; seen: boolean }) {
  if (applied) {
    return (
      <span
        className="badge-modern badge-modern--success"
        style={{ whiteSpace: 'nowrap', padding: '0.375rem 0.75rem', fontSize: '0.75rem' }}
      >
        <i className="fa fa-check me-1"></i>
        Applied
      </span>
    );
  }
  if (seen) {
    return (
      <span
        className="badge-modern badge-modern--info"
        style={{ whiteSpace: 'nowrap', padding: '0.375rem 0.75rem', fontSize: '0.75rem' }}
      >
        <i className="fa fa-eye me-1"></i>
        Seen
      </span>
    );
  }
  return (
    <span
      className="badge-modern badge-modern--warning"
      style={{ whiteSpace: 'nowrap', padding: '0.375rem 0.75rem', fontSize: '0.75rem' }}
    >
      <i className="fa fa-clock-o me-1"></i>
      Pending
    </span>
  );
}

// Column Definitions
const dataColumns = (timezone: string): TableColumn<SentInvitation>[] => [
  {
    name: "Candidate",
    selector: (row) => row.profile?.fullname || '',
    sortable: true,
    cell: (row) => (
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
        <a href={`/profiles/${row.profile?.id}`} target="_blank" rel="noopener noreferrer">
          <img
            src={row.profile?.avatar_url || '/images/missing.svg'}
            className="rounded-circle"
            alt={row.profile?.fullname}
            style={{
              width: '45px',
              height: '45px',
              objectFit: 'cover',
              border: '2px solid #e8ecef',
              transition: 'all 0.3s ease'
            }}
            onMouseOver={(e) => e.currentTarget.style.border = '2px solid #085ec8'}
            onMouseOut={(e) => e.currentTarget.style.border = '2px solid #e8ecef'}
          />
        </a>
        <div>
          <a
            href={`/profiles/${row.profile?.id}`}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              color: '#085ec8',
              textDecoration: 'none',
              fontWeight: 600,
              fontSize: '0.95rem',
              transition: 'color 0.3s ease'
            }}
            onMouseOver={(e) => e.currentTarget.style.color = '#71ad08'}
            onMouseOut={(e) => e.currentTarget.style.color = '#085ec8'}
          >
            {row.profile?.fullname}
          </a>
          {row.profile?.city && row.profile?.state && (
            <div style={{ fontSize: '0.8rem', color: '#6c757d', marginTop: '0.125rem' }}>
              <i className="fa fa-map-marker me-1"></i>
              {row.profile.city}, {row.profile.state}
            </div>
          )}
        </div>
      </div>
    ),
    grow: 2
  },
  {
    name: "Job",
    selector: (row) => row.job?.job_type?.title || row.job?.title || '',
    sortable: true,
    cell: (row) => {
      const jobTitle = row.job?.job_type?.title || row.job?.title || 'Unknown Job';
      const positionType = row.job?.position_type || 'permanent';

      return (
        <div>
          <a
            href={`/jobs/${row.job?.id}`}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              color: '#085ec8',
              textDecoration: 'none',
              fontWeight: 600,
              fontSize: '0.9rem',
              transition: 'color 0.3s ease'
            }}
            onMouseOver={(e) => e.currentTarget.style.color = '#71ad08'}
            onMouseOut={(e) => e.currentTarget.style.color = '#085ec8'}
          >
            {jobTitle}
          </a>
          <div style={{ marginTop: '0.25rem' }}>
            <span
              style={{
                backgroundColor: positionType === 'temp' ? '#f1630d' : '#176fc9',
                color: 'white',
                padding: '0.2rem 0.5rem',
                borderRadius: '0.25rem',
                fontSize: '0.7rem',
                fontWeight: 600
              }}
            >
              {positionType === 'temp' ? 'Temp' : 'Permanent'}
            </span>
          </div>
        </div>
      );
    },
    grow: 1.5,
    hide: MOBILE_BREAKPOINT
  },
  {
    name: "Sent Date",
    selector: (row) => row.created_at,
    sortable: true,
    cell: (row) => (
      <div style={{ fontSize: '0.875rem' }}>
        <div style={{ fontWeight: 500 }}>
          {moment(row.created_at).tz(timezone).format("MMM D, YYYY")}
        </div>
        <div style={{ color: '#6c757d', fontSize: '0.8rem' }}>
          {moment(row.created_at).tz(timezone).format("h:mm A")}
        </div>
      </div>
    ),
    grow: 1,
    hide: MOBILE_BREAKPOINT
  },
  {
    name: "Status",
    selector: (row) => row.applied ? 'applied' : (row.seen ? 'seen' : 'pending'),
    sortable: true,
    cell: (row) => <StatusBadge applied={row.applied} seen={row.seen} />,
    grow: 1
  },
  {
    name: "Response Date",
    selector: (row) => row.applied_date || row.seen_date || '',
    sortable: true,
    cell: (row) => {
      const responseDate = row.applied_date || row.seen_date;
      if (!responseDate) {
        return <span style={{ color: '#6c757d', fontSize: '0.875rem' }}>-</span>;
      }
      return (
        <div style={{ fontSize: '0.875rem' }}>
          <div style={{ fontWeight: 500 }}>
            {moment(responseDate).tz(timezone).format("MMM D, YYYY")}
          </div>
          <div style={{ color: '#6c757d', fontSize: '0.8rem' }}>
            {moment(responseDate).tz(timezone).format("h:mm A")}
          </div>
        </div>
      );
    },
    grow: 1,
    hide: MOBILE_BREAKPOINT
  },
  {
    name: "Actions",
    sortable: false,
    cell: (row) => (
      <div className="d-flex gap-2">
        <a
          href={`/profiles/${row.profile?.id}`}
          target="_blank"
          rel="noopener noreferrer"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '0.4rem 0.6rem',
            borderRadius: '0.375rem',
            fontSize: '0.8rem',
            fontWeight: 600,
            textDecoration: 'none',
            background: 'linear-gradient(135deg, #085ec8, #176fc9)',
            color: 'white',
            border: 'none',
            cursor: 'pointer',
            transition: 'all 0.3s ease',
            boxShadow: '0 2px 6px rgba(8, 94, 200, 0.25)'
          }}
          onMouseOver={(e) => {
            e.currentTarget.style.transform = 'translateY(-1px)';
            e.currentTarget.style.boxShadow = '0 4px 10px rgba(8, 94, 200, 0.35)';
          }}
          onMouseOut={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = '0 2px 6px rgba(8, 94, 200, 0.25)';
          }}
        >
          <i className="fa fa-user me-1"></i>
          Profile
        </a>
      </div>
    ),
    grow: 0.8,
    center: true
  }
];

// Main Component
export default function SentInvitations({
  invitations,
  timezone,
  totalCount,
  currentPage,
  perPage
}: SentInvitationsProps) {
  const [filters, setFilters] = React.useState({
    nameFilter: "",
    statusFilter: ""
  });

  // Filter logic (client-side filtering for current page data)
  const filteredInvitations = React.useMemo(() => {
    return invitations.filter((invitation) => {
      // Name filter
      if (filters.nameFilter &&
          !invitation.profile?.fullname?.toLowerCase().includes(filters.nameFilter.toLowerCase())) {
        return false;
      }

      // Status filter
      if (filters.statusFilter) {
        if (filters.statusFilter === 'applied' && !invitation.applied) return false;
        if (filters.statusFilter === 'seen' && (!invitation.seen || invitation.applied)) return false;
        if (filters.statusFilter === 'pending' && (invitation.seen || invitation.applied)) return false;
      }

      return true;
    });
  }, [invitations, filters]);

  const columns = dataColumns(timezone);

  // Handle page change
  const handlePageChange = (page: number) => {
    window.location.href = `/application_invites?page=${page}&per_page=${perPage}`;
  };

  // Handle rows per page change
  const handlePerRowsChange = (newPerPage: number, page: number) => {
    window.location.href = `/application_invites?page=${page}&per_page=${newPerPage}`;
  };

  return (
    <div className="container py-4">
      <div className="mb-4">
        <h1 style={{
          color: '#085ec8',
          fontWeight: 700,
          marginBottom: '0.5rem'
        }}>
          Sent Invitations
        </h1>
        <p style={{ color: '#6c757d', marginBottom: 0 }}>
          Track the status of invitations you have sent to candidates
        </p>
      </div>

      <FilterBar
        filters={filters}
        onFiltersChange={setFilters}
      />

      <div
        style={{
          background: 'white',
          border: '1px solid #e8ecef',
          borderRadius: '0.75rem',
          padding: '0.75rem',
          marginBottom: '1.5rem',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.06)',
          transition: 'box-shadow 0.3s ease',
          overflow: 'auto'
        }}
      >
        {filteredInvitations.length === 0 ? (
          <div style={{
            textAlign: 'center',
            padding: '3rem',
            color: '#6c757d'
          }}>
            <i className="fa fa-envelope-o fa-3x mb-3" style={{ opacity: 0.5 }}></i>
            <h5>No invitations found</h5>
            <p>
              {filters.nameFilter || filters.statusFilter
                ? "Try adjusting your filters"
                : "You haven't sent any invitations yet"}
            </p>
          </div>
        ) : (
          <DataTable
            data={filteredInvitations}
            columns={columns}
            pagination
            paginationServer
            paginationTotalRows={totalCount}
            paginationDefaultPage={currentPage}
            paginationPerPage={perPage}
            paginationRowsPerPageOptions={[10, 20, 50, 100]}
            onChangePage={handlePageChange}
            onChangeRowsPerPage={handlePerRowsChange}
            responsive
            customStyles={{
              responsiveWrapper: {
                style: {
                  overflowX: 'visible',
                  overflowY: 'visible'
                }
              },
              table: {
                style: {
                  width: '100%',
                  tableLayout: 'auto'
                }
              },
              headCells: {
                style: {
                  fontSize: '0.875rem',
                  fontWeight: 600,
                  color: '#6c757d',
                  paddingTop: '0.75rem',
                  paddingBottom: '0.75rem',
                  paddingLeft: '0.5rem',
                  paddingRight: '0.5rem'
                }
              },
              cells: {
                style: {
                  paddingLeft: '0.5rem',
                  paddingRight: '0.5rem',
                  paddingTop: '0.75rem',
                  paddingBottom: '0.75rem',
                  overflow: 'visible'
                }
              }
            }}
          />
        )}
      </div>

      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        color: '#6c757d',
        fontSize: '0.875rem'
      }}>
        <span>
          Showing {filteredInvitations.length} of {totalCount} invitations
        </span>
      </div>
    </div>
  );
}
